import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult, UseQueryOptions } from "@tanstack/react-query";

/**
 * Generischer Hook der TanStack Query wraps.
 * Konkrete Feature-Hooks (useHotspots, useWallet etc.) kommen in W1.x-Stories.
 *
 * F015: Exponential backoff fuer BE warmup race condition.
 * Exponential: 1s -> 2s -> 4s (base 1000, factor 2).
 *
 * retry und retryDelay sind konfigurierbar:
 * - Wenn in options übergeben, wird der übergebene Wert genutzt.
 * - Wenn nicht übergeben, greift der QueryClient-Default (kein hartes Override).
 *   Falls auch kein QueryClient-Default gesetzt ist, nutzt TanStack Query seinen
 *   eingebauten Default (retry: 3).
 *
 * MIGRATION F015: Den gewünschten retry-Default (z.B. 1) in query-client.ts als
 * defaultOptions.queries.retry setzen, oder explizit als options-Argument übergeben.
 */

type UseApiOptions<TData> = Pick<
  UseQueryOptions<TData>,
  'retry' | 'retryDelay' | 'enabled' | 'staleTime' | 'gcTime'
>

export function useApi<TData>(
  queryKey: unknown[],
  fetcher: () => Promise<{ data?: TData; error?: unknown }>,
  options?: UseApiOptions<TData>
): UseQueryResult<TData> {
  return useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await fetcher();
      if (error) throw error;
      // S10-21 F-064: queryFn must NEVER return undefined — React Query throws
      // "Query data cannot be undefined". If data is absent with no error, throw
      // a descriptive error so the query lands in error state rather than
      // crashing with an unhandled exception.
      if (data === undefined) {
        throw new Error('Query data is undefined — expected error was not set by fetcher');
      }
      return data as TData;
    },
    // Kein hartes retry hier — QueryClient-Default oder explizite Option entscheiden.
    // Standardmäßige retryDelay-Funktion (exponential backoff) gilt nur wenn retry aktiv.
    retryDelay: (attemptIndex) => Math.min(1000 * Math.pow(2, attemptIndex), 10000),
    ...options,
  });
}
